import { Flex, Tag, Typography } from "antd"
import { type ColumnsType } from "antd/es/table"
import React from "react"
import { Link } from "react-router-dom"

import { empty } from "../../../constants/placeholders"
import { invoiceColumnTitle } from "./invoice-column-titles.tsx"
import { RoutesPaths } from "../../../constants/routes.ts"
import { budgetFormat } from "../../../utils/number/budget-format.ts"
import {
  INVOICE_STATUSES_MAP,
  type InvoiceStatuses
} from "../../Project/ProjectViewPage/constants/invoice-statuses.ts"
import {
  type InvoiceRowAction,
  InvoiceRowActions
} from "../helpers/invoice-row-actions.tsx"
import { InvoiceEditButton } from "./InvoiceEditButton.tsx"
import { InvoiceNumberCell } from "./InvoiceNumberCell.tsx"
import {
  INVOICE_ORIGINS,
  type InvoiceListItem
} from "../types/invoices.types.ts"

const { Text } = Typography

export type { InvoiceRowAction }

/**
 * Columns a user sees only after switching them on in Table settings. With all
 * ten on, Paid to Date and Status started off-screen on a 1440px laptop and
 * Amount was cut to "Am" under the pinned Actions column. Date Issued is the
 * list's default sort, so it stays visible. A saved layout wins either way.
 */
export const INVOICE_DEFAULT_HIDDEN_COLUMNS = ["origin"] as const

type Translate = (key: string, options?: Record<string, unknown>) => string

interface GetColumnsArgs {
  t: Translate
  editAllowed?: boolean
  /** Whether this caller may edit this row: their own, or anyone's with manage-others. */
  canEditRecord?: (record: InvoiceListItem) => boolean
  voidAllowed?: boolean
  /** A row is open for inline edit, so every other row's actions wait. */
  isLocked?: boolean
  onSelectRow: (record: InvoiceListItem, action: InvoiceRowAction) => void
  onEdit?: (record: InvoiceListItem) => void
  // "Add a Branch column to combined invoice lists" — shown only while the
  // global selector is "All branches", where a row can genuinely belong to
  // any of several branches; a single-branch workspace already implies
  // the branch and would only repeat it on every row.
  showBranchColumn?: boolean
}

export const getColumns = (
  args: GetColumnsArgs
): ColumnsType<InvoiceListItem> => {
  const {
    t,
    editAllowed,
    voidAllowed,
    isLocked,
    onSelectRow,
    onEdit,
    showBranchColumn
  } = args
  const canEdit = (record: InvoiceListItem) =>
    Boolean(editAllowed) && (args.canEditRecord?.(record) ?? true)

  return [
    {
      title: t("invoices.columns.invoiceNumber"),
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 120,
      render: (text: string) => (
        <InvoiceNumberCell number={text} t={t} />
      )
    },
    ...(showBranchColumn
      ? [
          {
            title: "Branch",
            dataIndex: "branch_name",
            key: "branch_name",
            width: 140,
            render: (branchName: string | null) => branchName || empty
          }
        ]
      : []),
    {
      title: t("invoices.columns.project"),
      dataIndex: "project_info",
      key: "project",
      width: 160,
      ellipsis: true,
      render: (projectInfo: InvoiceListItem["project_info"]) =>
        projectInfo ? (
          <Link
            to={RoutesPaths.project.view(projectInfo.id).invoices}
            className="text-primary hover:underline"
          >
            {[projectInfo.uid, projectInfo.lead_name]
              .filter(Boolean)
              .join(" — ") || empty}
          </Link>
        ) : (
          <Text type="secondary">{t("invoices.noProject")}</Text>
        )
    },
    {
      title: invoiceColumnTitle(t, "origin"),
      dataIndex: "origin_display",
      key: "origin",
      width: 130,
      render: (label: string, record) => (
        <Tag
          color={
            record.origin === INVOICE_ORIGINS.GENERATED ? "blue" : "default"
          }
          className="m-0 rounded-full px-2.5 py-0.5 text-xs"
        >
          {label || empty}
        </Tag>
      )
    },
    {
      title: t("invoices.columns.customer"),
      dataIndex: "customer_name",
      key: "customer_name",
      width: 150,
      ellipsis: true,
      render: (name: string | null) => name || empty
    },
    {
      title: t("invoices.columns.dateIssued"),
      dataIndex: "date_issued",
      key: "date_issued",
      sorter: true,
      defaultSortOrder: "descend",
      width: 120,
      render: (value: string | null) => (
        <Text type="secondary">{value || empty}</Text>
      )
    },
    {
      title: t("invoices.columns.dueDate"),
      dataIndex: "due_date",
      key: "due_date",
      sorter: true,
      width: 120,
      render: (value: string | null) => (
        <Text type="secondary">{value || empty}</Text>
      )
    },
    {
      title: t("invoices.columns.amount"),
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      width: 130,
      align: "right",
      render: (value: string) => <Text strong>{budgetFormat(value)}</Text>
    },
    {
      title: invoiceColumnTitle(t, "paidToDate"),
      dataIndex: "paid_to_date",
      key: "paid_to_date",
      width: 130,
      align: "right",
      render: (value: string) => <Text>{budgetFormat(value)}</Text>
    },
    {
      title: invoiceColumnTitle(t, "status"),
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: InvoiceStatuses, record) => {
        const statusData = INVOICE_STATUSES_MAP?.[status]
        return (
          <Tag
            style={statusData?.style}
            className="m-0 rounded-full border-0 px-2.5 py-0.5 text-xs font-medium"
          >
            {record?.status_display || statusData?.label || empty}
          </Tag>
        )
      }
    },
    {
      title: t("invoices.columns.actions"),
      key: "actions",
      width: 100,
      fixed: "right",
      className: "text-center",
      render: (_, record) => (
        <Flex gap="small" justify="center" align="center">
          {canEdit(record) && onEdit && (
            <InvoiceEditButton
              record={record}
              t={t}
              disabled={isLocked}
              onEdit={() => onEdit(record)}
            />
          )}
          <InvoiceRowActions
            record={record}
            t={t}
            viewEnabled
            editAllowed={canEdit(record)}
            voidAllowed={voidAllowed}
            disabled={isLocked}
            onSelectRow={(action) => onSelectRow(record, action)}
          />
        </Flex>
      )
    }
  ]
}
